import React, { useState, useEffect } from 'react';
import { FaTimes, FaDownload, FaSpinner } from 'react-icons/fa';
import { useColorScheme } from '../contexts/ColorSchemeContext';

const ResumeModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError(null);
      
      // Test if PDF is accessible
      fetch(`${process.env.PUBLIC_URL}/resume.pdf`, { method: 'HEAD' })
        .then(response => {
          console.log('PDF fetch test:', response.status, response.headers.get('content-type'));
          if (!response.ok) {
            throw new Error(`PDF not accessible: ${response.status}`);
          }
          // If accessible, stop loading after a short delay
          setTimeout(() => setIsLoading(false), 1000);
        })
        .catch(error => {
          console.error('PDF accessibility test failed:', error);
          setError(`PDF file not accessible: ${error.message}`);
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL}/resume.pdf`;
    link.download = 'Ashraf_Ali_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className={`${colors.cardBackground} rounded-2xl shadow-2xl w-[80vw] max-w-4xl h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            View Resume
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
          >
            <FaTimes className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FaSpinner 
                  className="w-8 h-8 animate-spin mx-auto mb-4" 
                  style={{ color: 'var(--color-primary)' }}
                />
                <p className="text-gray-600 dark:text-gray-300">Loading PDF...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-red-500 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 text-gray-900 rounded-lg transition-colors duration-200 accent-gradient accent-gradient-hover"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!isLoading && !error && (
            <div className="flex-1 p-6">
              <div className="h-full bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <iframe
                  src={`${process.env.PUBLIC_URL}/resume.pdf#toolbar=1&navpanes=0&scrollbar=1&zoom=100&view=FitV`}
                  className="w-full h-full border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg"
                  title="Resume PDF"
                  onLoad={() => console.log('PDF iframe loaded successfully')}
                  onError={(e) => {
                    console.error('PDF iframe error:', e);
                    setError('Failed to load PDF in iframe');
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer with download button */}
        {!isLoading && !error && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex justify-center">
              <button
                onClick={handleDownload}
                className="flex items-center space-x-2 px-6 py-3 accent-gradient text-gray-900 font-semibold rounded-full accent-gradient-hover transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <FaDownload className="w-4 h-4" />
                <span>Download PDF</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResumeModal;
